'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef } from 'react';
import { Bookmark } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSession } from '@/lib/auth-client';
import { useSignInModal } from '@/lib/ui';
interface TrackComponentButtonProps {
  /** Whether this component is currently tracked */
  isTracked: boolean;
  /** Callback to toggle tracking (called after auth is confirmed) */
  onToggle: () => void;
  /** Whether the mutation is in flight */
  isToggling?: boolean;
  /** Additional className for the button */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md';
}

/**
 * Reusable button for tracking/flagging components.
 * Shows a bookmark icon that fills when tracked.
 * If user is not signed in, shows sign-in modal first.
 */
export function TrackComponentButton(t0) {
  const $ = _c(21);
  const {
    isTracked,
    onToggle,
    isToggling: t1,
    className,
    size: t2
  } = t0;
  const isToggling = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "sm" : t2;
  const {
    data: session
  } = useSession();
  const {
    showSignInModal
  } = useSignInModal();
  const pendingRef = useRef(false);
  let t3;
  if ($[0] !== onToggle || $[1] !== session?.user || $[2] !== showSignInModal) {
    t3 = e => {
      e.preventDefault();
      e.stopPropagation();
      if (!session?.user) {
        pendingRef.current = true;
        showSignInModal({
          description: <div className="font-bold p-6"><div className="text-lg">Sign in to track this for later</div><div className="text-sm text-gray-500 font-normal mt-1">Sign up takes 20 seconds</div></div>,
          preferFullscreenForNewUsers: false,
          onSuccess: () => {
            if (pendingRef.current) {
              pendingRef.current = false;
              onToggle();
            }
          }
        });
        return;
      }
      onToggle();
    };
    $[0] = onToggle;
    $[1] = session?.user;
    $[2] = showSignInModal;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleClick = t3;
  const iconSize = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  const t4 = isTracked ? "Remove from tracked" : "Track for later";
  const t5 = isTracked ? "Remove from tracked" : "Track for later";
  const t6 = size === "sm" ? "w-6 h-6" : "w-7 h-7";
  const t7 = isTracked ? "text-amber-600 bg-amber-50 border border-amber-200 hover:bg-amber-100" : "text-gray-400 bg-gray-50 border border-gray-200 hover:text-amber-500 hover:bg-amber-50 hover:border-amber-200";
  const t8 = isToggling && "opacity-50 cursor-not-allowed";
  let t9;
  if ($[4] !== className || $[5] !== t6 || $[6] !== t7 || $[7] !== t8) {
    t9 = cn("inline-flex items-center justify-center rounded-md transition-all cursor-pointer", t6, t7, t8, className);
    $[4] = className;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const t10 = isTracked && "fill-amber-500";
  let t11;
  if ($[9] !== iconSize || $[10] !== t10) {
    t11 = cn(iconSize, t10);
    $[9] = iconSize;
    $[10] = t10;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== t11) {
    t12 = <Bookmark className={t11} />;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== handleClick || $[15] !== isToggling || $[16] !== t12 || $[17] !== t4 || $[18] !== t5 || $[19] !== t9) {
    t13 = <button type="button" onClick={handleClick} disabled={isToggling} title={t4} aria-label={t5} className={t9}>{t12}</button>;
    $[14] = handleClick;
    $[15] = isToggling;
    $[16] = t12;
    $[17] = t4;
    $[18] = t5;
    $[19] = t9;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  return t13;
}